import { client } from "@client";
import { ErrorComponent } from "@components/ErrorComponent";
import { ExclusiveLabel } from "@components/ExclusiveLabel";
import { Row } from "@components/Flex";
import { ProjectionBreakdown } from "@components/Goals";
import { AccountsSection } from "@components/Goals/AccountsSection";
import { GoalDeposit } from "@components/Goals/GoalDeposit";
import { GoalIcon } from "@components/Goals/GoalIcon";
import { GoalInvestmentBreakdown } from "@components/Goals/GoalInvestmentBreakdown";
import { GoalInvestmentPerformance } from "@components/Goals/GoalInvestmentPerformance";
import { GoalProgressBar } from "@components/Goals/GoalProgressBar";
import { PerformanceBreakdown } from "@components/Goals/PerformanceBreakdown";
import { SharedWithSection } from "@components/Goals/SharedWithSection";
import { ShareGoalCard } from "@components/Goals/ShareGoalCard";
import { Loader } from "@components/Loader";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body, Title4 } from "@components/Typography";
import { NODE_COUNT } from "@constants";
import { GoalType } from "@ctypes";
import { SENT_SHARE_GOAL_INVITES } from "@graphql";
import {
  useGoalDetailsBreakdown,
  useGoalsContext,
  usePerformanceChartNodes,
  useUser,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { useEffect } from "react";
import { BalanceSyncBanner } from "./BalanceSyncBanner";
import { MoreMenu } from "./MoreMenu";
import { Container, InnerContainer, useStyles } from "./styles";
import { TrumpSidecarDetails } from "./TrumpSidecarDetails";

/** Legacy layout, kept for the Trump-sidecar goal type only. */
export const SidecarBreakdown = () => {
  const styles = useStyles();

  const {
    beneficiaryId,
    goalId,
    goalType,
    goalLengthInYears,
    multiplier,
    contributeAfter18,
    annualDeposit,
    numberOfYearsActive,
    initialBalance,
  } = useGoalsContext();

  const { loading: userLoading, error } = useUser();

  const { data: goalDetails, loading: goalDetailsLoading } = useGoalDetailsBreakdown({
    variables: {
      beneficiaryId,
      goalId: goalId!,
      nodeCount: NODE_COUNT,
    },
  });

  useEffect(() => {
    client.query({
      query: SENT_SHARE_GOAL_INVITES,
      variables: {
        goalId,
      },
    });
  }, []);

  const nodes = usePerformanceChartNodes({ performance: goalDetails });

  const props: MainLayoutProps = {
    pageHeader: <GoalsBreakdownHeader />,
    rightHeader: <MoreMenu />,
  };

  const loading = userLoading || goalDetailsLoading;
  const isTrumpSidecar = goalType === GoalType.TrumpAccountSidecar;

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <MainLayout {...props} containerStyle={styles.container}>
      <Spacing size={SpacingSizes.M} />

      <Container>
        <div style={styles.cardContainer}>
          <InnerContainer style={styles.bannerInnerContainer}>
            <BalanceSyncBanner />
          </InnerContainer>
        </div>
      </Container>

      <Container>
        <PerformanceBreakdown data={nodes} />
        <div style={styles.divider} />
        <ProjectionBreakdown
          numberOfYearsActive={numberOfYearsActive}
          projectionLength={goalLengthInYears}
          annualDeposit={annualDeposit}
          multiplier={multiplier}
          contributeAfter18={contributeAfter18}
          initialBalance={initialBalance}
        />
      </Container>
      <Spacing size={SpacingSizes.M} />

      {isTrumpSidecar ? (
        <Container>
          <div style={styles.cardContainer}>
            <InnerContainer style={styles.cardInnerContainer}>
              <TrumpSidecarDetails />
            </InnerContainer>
          </div>
        </Container>
      ) : null}

      <Container>
        <div style={styles.cardContainer}>
          <InnerContainer style={styles.cardInnerContainer}>
            {!isTrumpSidecar ? <GoalProgressBar style={styles.leftCard} /> : null}
            <GoalDeposit style={isTrumpSidecar ? styles.leftCard : styles.rightCard} />
          </InnerContainer>
        </div>
      </Container>
      <Spacing size={SpacingSizes.M} />

      <Container>
        <div style={styles.cardContainer}>
          <InnerContainer style={styles.cardInnerContainer}>
            <GoalInvestmentPerformance style={styles.leftCard} />
            <ShareGoalCard style={styles.rightCard} />
          </InnerContainer>
        </div>
      </Container>
      <Spacing size={SpacingSizes.M} />

      <Container>
        <div style={styles.cardContainer}>
          <InnerContainer style={styles.cardInnerContainer}>
            <GoalInvestmentBreakdown style={styles.half} />
            <AccountsSection style={styles.half} />
          </InnerContainer>
        </div>
      </Container>
      <Spacing size={SpacingSizes.M} />

      <Container>
        <SharedWithSection />
      </Container>
    </MainLayout>
  );
};

const GoalsBreakdownHeader = () => {
  const styles = useStyles();

  const { goalName, goalType, beneficiary } = useGoalsContext();

  const beneficiaryName = beneficiary?.firstName;

  if (!goalName) {
    return <Title4>{i18n.t(Translations.GoalBreakdownRouteHeader)}</Title4>;
  }

  return (
    <Row style={styles.headerContainer}>
      {goalType ? <GoalIcon type={goalType} /> : null}
      <Body style={styles.spacing}>{goalName}</Body>
      {beneficiaryName ? (
        <ExclusiveLabel style={styles.exclusive}>{beneficiaryName}</ExclusiveLabel>
      ) : null}
    </Row>
  );
};
